import { customAlphabet } from 'nanoid';

/**
 * Props shared by block elements that carry a document-unique locationId.
 */
export interface LocationIdProps {
  /**
   * Unique identifier for the block element. Optional for backwards compatibility.
   */
  locationId?: string;
}

/**
 * The actual locationId generator.
 */
const _generateLocationId = customAlphabet(
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
  12
);

export const TEST_LOCATION_ID = 'TEST_LOCATION_ID';

/**
 * Generate a locationId for a block element.
 *
 * In unit tests, we need to generate a predictable locationId for a block element.
 * We make them all return 'TEST_LOCATION_ID' in unit tests.
 */
export function generateLocationId() {
  if (process.env.NODE_ENV === 'test' || process.env.ENV === 'ci') return TEST_LOCATION_ID;

  return _generateLocationId();
}

/**
 * In the LLM-facing HTML document format, table and table-row ids are the
 * element's location anchor with a type prefix (`table-abc123`, `row-abc123`)
 * so a reader can tell what kind of element an id names. The prefix exists
 * only in that LLM-facing layer — stored locationIds are never prefixed.
 *
 * Parsing is unambiguous: DOCX anchors are 12 alphanumeric chars (never
 * contain `-`) and PDF anchors are `{pageNum}:{x1}:{y1}:{x2}:{y2}` coordinate
 * strings (always start with a digit), so neither can start with a prefix.
 */
export const TABLE_LOCATION_ID_PREFIX = 'table-';
export const ROW_LOCATION_ID_PREFIX = 'row-';

export function formatTableLocationId(locationAnchor: string): string {
  return `${TABLE_LOCATION_ID_PREFIX}${locationAnchor}`;
}

export function formatRowLocationId(locationAnchor: string): string {
  return `${ROW_LOCATION_ID_PREFIX}${locationAnchor}`;
}

export type ParsedAnchorId = {
  kind: 'paragraph' | 'row' | 'table';
  /** The anchor with any type prefix stripped — a bare locationId or PDF coordinate string. */
  locationAnchor: string;
};

/**
 * Inverse of `parseAnchorId`: renders the LLM-facing id for an anchor of the
 * given kind — prefixed for tables and rows, bare for paragraphs.
 */
export function formatAnchorId(kind: ParsedAnchorId['kind'], locationAnchor: string): string {
  if (kind === 'table') return formatTableLocationId(locationAnchor);
  if (kind === 'row') return formatRowLocationId(locationAnchor);
  return locationAnchor;
}

/**
 * Classifies an id received from the LLM by its type prefix. An unprefixed id
 * is a paragraph anchor (paragraphs are serialized without a prefix).
 */
export function parseAnchorId(id: string): ParsedAnchorId {
  if (id.startsWith(TABLE_LOCATION_ID_PREFIX)) {
    return { kind: 'table', locationAnchor: id.slice(TABLE_LOCATION_ID_PREFIX.length) };
  }
  if (id.startsWith(ROW_LOCATION_ID_PREFIX)) {
    return { kind: 'row', locationAnchor: id.slice(ROW_LOCATION_ID_PREFIX.length) };
  }
  return { kind: 'paragraph', locationAnchor: id };
}
